// [NEW] EventDetailScreen Component - Detail event dengan pilihan kategori tiket
import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db, appId } from '../firebase';
import { HiOutlineCalendar, HiOutlineLocationMarker, HiOutlineClock, HiOutlineTicket, HiOutlineUsers, HiOutlineX } from 'react-icons/hi';

const EventDetailScreen = ({ eventId, user, onBack, onPurchase }) => {
    const [event, setEvent] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [showPurchaseModal, setShowPurchaseModal] = useState(false);

    useEffect(() => {
        const fetchEvent = async () => {
            setIsLoading(true);
            const eventDoc = await getDoc(doc(db, "artifacts", appId, "public", "data", "events", eventId));
            if (eventDoc.exists()) {
                setEvent({ id: eventDoc.id, ...eventDoc.data() });
            }
            setIsLoading(false);
        };

        if (eventId) {
            fetchEvent();
        }
    }, [eventId]);

    const handleCategorySelect = (category) => {
        setSelectedCategory(category);
        setShowPurchaseModal(true);
    };

    const handlePurchaseConfirm = () => {
        if (!selectedCategory) return;

        // Close local modal
        setShowPurchaseModal(false);

        // Pass to parent component for payment flow
        if (onPurchase) {
            onPurchase({
                event,
                category: selectedCategory
            });
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700"></div>
            </div>
        );
    }

    if (!event) {
        return (
            <div className="min-h-screen bg-gray-50 p-4">
                <div className="text-center py-12">
                    <p className="text-gray-500">Event tidak ditemukan</p>
                    <button onClick={onBack} className="mt-4 text-green-700 underline">
                        Kembali
                    </button>
                </div>
            </div>
        );
    }

    // Get available categories or create default
    const categories = event.ticketCategories && event.ticketCategories.length > 0
        ? event.ticketCategories.filter(c => c.enabled)
        : [{
            id: 'default',
            name: 'General Admission',
            pricingType: event.price > 0 ? 'fixed' : 'free',
            price: event.price || 0,
            quota: event.quota || 100,
            sold: event.sold || 0,
            description: event.description || ''
        }];

    return (
        <div className="min-h-screen bg-gray-50 pb-24">
            {/* Header with back button */}
            <div className="sticky top-0 bg-white z-10 px-4 py-3 border-b flex items-center">
                <button onClick={onBack} className="mr-4 p-2 hover:bg-gray-100 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
                </button>
                <h2 className="font-semibold text-gray-800">Detail Event</h2>
            </div>

            {/* Event Banner */}
            {event.imageUrl && (
                <img
                    src={event.imageUrl}
                    alt={event.title}
                    className="w-full h-64 object-cover"
                />
            )}

            {/* Event Info */}
            <div className="bg-white p-6 mb-4">
                <h1 className="text-2xl font-bold text-green-900 mb-4">{event.title}</h1>

                <div className="space-y-3 text-gray-700">
                    <div className="flex items-start">
                        <HiOutlineCalendar className="w-5 h-5 mr-3 text-green-700 mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="font-medium">
                                {event.date?.toDate?.()?.toLocaleDateString('id-ID', {
                                    weekday: 'long',
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric'
                                })}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-start">
                        <HiOutlineClock className="w-5 h-5 mr-3 text-green-700 mt-0.5 flex-shrink-0" />
                        <div>
                            <p className="font-medium">
                                {event.date?.toDate?.()?.toLocaleTimeString('id-ID', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })} WIB
                            </p>
                        </div>
                    </div>

                    {event.location && (
                        <div className="flex items-start">
                            <HiOutlineLocationMarker className="w-5 h-5 mr-3 text-green-700 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="font-medium">{event.location}</p>
                            </div>
                        </div>
                    )}
                </div>

                {event.description && (
                    <div className="mt-6">
                        <h3 className="font-semibold text-gray-800 mb-2">Tentang Event</h3>
                        <p className="text-gray-600 whitespace-pre-line">{event.description}</p>
                    </div>
                )}

                {/* Organizer Info */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Diselenggarakan oleh</p>
                    <p className="font-semibold text-gray-800">{event.organizerName || 'Semesta Event'}</p>
                </div>
            </div>

            {/* Ticket Categories */}
            <div className="bg-white p-6">
                <h3 className="font-bold text-gray-800 mb-4 flex items-center">
                    <HiOutlineTicket className="w-5 h-5 mr-2 text-green-700" />
                    Pilih Tiket
                </h3>

                <div className="space-y-3">
                    {categories.map((category) => {
                        const isSoldOut = category.sold >= category.quota;
                        const availableStock = category.quota - (category.sold || 0);

                        return (
                            <div
                                key={category.id}
                                className={`border rounded-xl p-4 transition ${isSoldOut ? 'bg-gray-50 opacity-60' : 'hover:border-green-500 cursor-pointer'
                                    }`}
                                onClick={() => !isSoldOut && handleCategorySelect(category)}
                            >
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex-1">
                                        <h4 className="font-bold text-gray-800">{category.name}</h4>
                                        {category.description && (
                                            <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                                        )}
                                    </div>

                                    <div className="text-right ml-4">
                                        {category.pricingType === 'free' ? (
                                            <span className="text-lg font-bold text-green-700">GRATIS</span>
                                        ) : category.pricingType === 'pay-as-you-wish' ? (
                                            <div>
                                                <span className="text-sm text-gray-500">Mulai dari</span>
                                                <p className="text-lg font-bold text-green-700">
                                                    Rp {(category.minimumPrice || 0).toLocaleString('id-ID')}
                                                </p>
                                            </div>
                                        ) : (
                                            <span className="text-lg font-bold text-green-700">
                                                Rp {category.price.toLocaleString('id-ID')}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="flex justify-between items-center mt-3 text-sm">
                                    {isSoldOut ? (
                                        <span className="text-red-600 font-semibold">SOLD OUT</span>
                                    ) : (
                                        <span className="text-gray-500">
                                            <HiOutlineUsers className="inline w-4 h-4 mr-1" />
                                            {availableStock} tiket tersedia
                                        </span>
                                    )}

                                    {!isSoldOut && (
                                        <span className="text-green-700 font-medium">Pilih →</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Voucher Bundling Info */}
            {event.enableVoucherBundling && (
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 mx-4 mt-4 rounded-xl border border-purple-200">
                    <p className="text-sm font-semibold text-purple-900 mb-1">🎁 Bonus Spesial!</p>
                    <p className="text-sm text-purple-700">
                        Dapatkan voucher diskon Semesta Cafe saat membeli tiket event ini
                    </p>
                </div>
            )}

            {/* Purchase Modal */}
            {showPurchaseModal && selectedCategory && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end md:items-center justify-center p-4">
                    <div className="bg-white rounded-t-3xl md:rounded-2xl w-full max-w-md p-6 animate-slide-up">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold text-gray-800">Konfirmasi Pembelian</h3>
                            <button onClick={() => setShowPurchaseModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                                <HiOutlineX className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                            <p className="text-sm text-gray-500">Event</p>
                            <p className="font-bold text-gray-800">{event.title}</p>

                            <div className="border-t border-gray-200 my-3"></div>

                            <p className="text-sm text-gray-500">Kategori Tiket</p>
                            <p className="font-bold text-gray-800">{selectedCategory.name}</p>

                            <div className="border-t border-gray-200 my-3"></div>

                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-500">Total Bayar</span>
                                <span className="text-xl font-bold text-green-700">
                                    {selectedCategory.pricingType === 'free' ? 'GRATIS' :
                                        selectedCategory.pricingType === 'pay-as-you-wish' ?
                                            `Min. Rp ${(selectedCategory.minimumPrice || 0).toLocaleString('id-ID')}` :
                                            `Rp ${selectedCategory.price.toLocaleString('id-ID')}`
                                    }
                                </span>
                            </div>
                        </div>

                        <button
                            onClick={handlePurchaseConfirm}
                            className="w-full bg-green-700 text-white py-4 rounded-xl font-bold text-lg hover:bg-green-800 transition"
                        >
                            Lanjut ke Pembayaran
                        </button>

                        <button
                            onClick={() => setShowPurchaseModal(false)}
                            className="w-full mt-3 text-gray-600 py-3"
                        >
                            Batal
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventDetailScreen;
